import classNames from "classnames"
import type { ReactNode, MouseEvent, ForwardedRef } from "react"
import { useCallback, forwardRef } from "react"
import Link from "~/components/Link"
import type { WithProductCardFragment, WithProductFragment } from "~/graphql"
import { darkenHexColor } from "~/helpers/colors"
import { useAnalytics } from "~/providers/AnalyticsProvider"

export interface Props {
  children: React.ReactNode
  variant?: "primary" | "secondary" | "action" | "white" | "danger"
  size?: "small" | "medium" | "large" | "xlarge" | "responsive"
  style?: "normal" | "inverted" | "outlined" | "dropShadow"
  textAlign?: "left" | "center" | "right"
  removePadding?: boolean
  full?: boolean | { sm?: boolean; md?: boolean; lg?: boolean }
  disabled?: boolean
  customBackgroundColor?: string | undefined | null
  customTextColor?: string | undefined | null
  icon?: ReactNode
  iconOnly?: boolean
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  trackClick?: () => {
    event: string
    data: Record<
      string,
      | string
      | number
      | null
      | WithProductFragment
      | WithProductCardFragment
      | undefined
    >
    frontendBase?: string
  }
  ariaLabel?: string
  ariaLabelledBy?: string
  pulse?: boolean
  customBorderColor?: string
}

interface LinkProps extends Props {
  url: string
  openAsExternal?: boolean
  openInNewWindow?: boolean
}

interface ButtonProps extends Props {
  url?: undefined
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  type?: "button" | "submit"
}

const Button = forwardRef<HTMLButtonElement, LinkProps | ButtonProps>(
  (
    {
      variant = "primary",
      size = "medium",
      style = "normal",
      textAlign = "center",
      removePadding,
      full,
      disabled,
      customBackgroundColor,
      customTextColor,
      icon,
      iconOnly,
      trackClick,
      children,
      onClick,
      ariaLabel,
      ariaLabelledBy,
      pulse,
      customBorderColor,
      ...props
    },
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const { track } = useAnalytics()
    const onClickWrapped = useCallback(
      (e: any) => {
        if (trackClick) {
          track(trackClick().event, trackClick().data)
        }
        if (onClick) onClick(e)
      },
      [trackClick, onClick, track]
    )

    const commonProps = {
      className: classNames(
        "flex items-center enabled:cursor-pointer disabled:opacity-25",
        variant === "secondary" ? "rounded-lg" : "rounded-full",
        {
          "w-full": full === true,
          "w-full md:w-auto": typeof full === "object" && full.sm,
          "w-full lg:w-auto": typeof full === "object" && full.md,
          "w-full xl:w-auto": typeof full === "object" && full.lg,
          "focus:ring-blue-dark-500": !customBackgroundColor
        }
      ),
      disabled,
      onClick: onClickWrapped,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy
    }

    const styleNormalClasses = {
      "bg-camp-primary text-white": variant == "primary",
      "bg-blue-dark-500 text-white": variant == "action",
      "bg-white text-blue-dark-500 border border-blue-dark-500":
        variant == "secondary",
      "bg-red-500 text-white": variant == "danger",
      "bg-white text-gray-300": variant == "white"
    }

    const styleOutlinedClasses = {
      "border-camp-primary text-camp-primary": variant == "primary",
      "border-text-blue-dark-500 text-blue-dark-500": variant == "action",
      "border-text-teal-500 text-teal-500": variant == "secondary",
      "border-white text-white": variant == "white",
      "border-red-500 text-red-500": variant == "danger"
    }

    const variantClasses =
      style == "outlined" || style == "inverted"
        ? styleOutlinedClasses
        : styleNormalClasses

    const sizeClasses = {
      "h-8 text-xs": size == "small",
      "h-10 text-sm": size == "medium",
      "h-12 text-base font-medium": size == "large",
      "h-14 text-base font-medium": size == "xlarge",
      "h-10 text-sm font-base lg:h-12 lg:text-base lg:font-medium":
        size == "responsive"
    }

    const buttonStyle =
      style == "outlined" || style == "inverted"
        ? {
            borderColor: customBackgroundColor
              ? darkenHexColor(customBackgroundColor, 20)
              : undefined,
            color: customTextColor ?? undefined
          }
        : style == "dropShadow"
          ? {
              borderColor: customBorderColor ?? undefined,
              borderBottom: `2px solid ${customBorderColor ?? "undefined"}`,
              borderRight: `2px solid ${customBorderColor ?? "undefined"}`,
              backgroundColor: customBackgroundColor ?? undefined,
              boxShadow: `0.25rem 0.25rem ${customBackgroundColor}`,
              color: customTextColor ?? undefined
            }
          : {
              backgroundColor: customBackgroundColor ?? undefined,
              color: customTextColor ?? undefined
            }

    const iconOnlySizeClasses = {
      "w-8 h-8": size == "small",
      "w-10 h-10": size == "medium",
      "w-12 h-12": size == "large" || size == "xlarge",
      "w-10 h-10 lg:w-12 lg:h-12": size == "responsive"
    }

    const content = (
      <div
        className={classNames(
          "flex items-center whitespace-nowrap",
          variant === "secondary" ? "rounded-lg" : "rounded-full",
          variantClasses,
          iconOnly ? iconOnlySizeClasses : sizeClasses,
          iconOnly
            ? { "justify-center": true }
            : {
                "w-full": true,
                "px-3": size == "small" && !removePadding,
                "px-3 md:px-5": size == "medium" && !removePadding,
                "px-7": (size == "large" || size == "xlarge") && !removePadding,
                "px-3 md:px-5 lg:px-7": size == "responsive" && !removePadding,
                "justify-center": textAlign == "center" && !icon,
                "justify-start": textAlign == "left" && !icon,
                "justify-end": textAlign == "right" && !icon,
                "justify-between": !!icon
              },
          {
            "motion-safe:animate-colorPulse": pulse,
            "border-[1.5px]": style == "outlined",
            "bg-white": style == "inverted" || style == "outlined"
          }
        )}
        style={buttonStyle}
      >
        {pulse ? (
          <div
            className="absolute w-[55%] rounded-full h-2/3 animate-ping m-auto z-0"
            style={buttonStyle}
          />
        ) : null}
        {iconOnly ? icon : children}
        {!iconOnly && icon && <span className="ml-4">{icon}</span>}
      </div>
    )

    if (props.url != undefined) {
      return (
        <Link
          to={!disabled ? props.url : undefined}
          trackClick={trackClick}
          openInNewWindow={props.openInNewWindow}
          openAsExternal={props.openAsExternal}
          {...commonProps}
        >
          {content}
        </Link>
      )
    } else {
      return (
        <button type={props.type ?? "button"} {...commonProps} ref={ref}>
          {content}
        </button>
      )
    }
  }
)

Button.displayName = "Button"

export default Button
